import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { LoaderStylesService } from '@frontend/loaders';
import { PrettyUrlsConfig, SkeletonConfig } from '@frontend/sports/common/client-config-data-access';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { DeviceService, Page } from '@frontend/vanilla/core';
import { BehaviorSubject, EMPTY, Observable, delay, distinctUntilChanged, filter, of, switchMap, withLatestFrom } from 'rxjs';

import { SlotLayoutRouteOverrideService } from '../bootstrap/slot-layout.bootstrap.service';
import { SubNavigationComponent } from '../sub-navigation/sub-navigation.component';
import { SkeletonPage } from './skeleton-page.model';

@Injectable({ providedIn: 'root' })
export class SkeletonService {
    private router = inject(Router);
    private urlConfig = inject(PrettyUrlsConfig);
    private device = inject(DeviceService);
    private skeletonConfig = inject(SkeletonConfig);
    private slotLayoutRouteOverrideService = inject(SlotLayoutRouteOverrideService);
    private stylesService = inject(LoaderStylesService);
    private page = inject(Page);
    private productUtils = inject(SportsProductUtils);

    private skeletonOutput$ = new BehaviorSubject<SkeletonPage | undefined>(undefined);

    get skeletonPage$(): Observable<SkeletonPage | undefined> {
        return this.skeletonOutput$.asObservable();
    }

    private readonly homeLogo = `/${this.page.lang}`;
    private readonly homeRgx = new RegExp(`/${this.page.lang}` + '/sports$');
    private readonly eventRgx = new RegExp(`/${this.page.lang}` + `/sports/${this.urlConfig.translations.events}$`);
    private readonly horseRacing = new RegExp(`/${this.page.lang}` + '/sports/+?([A-Za-z0-9]+?(-?[A-Za-z0-9]+)+)?-29');
    private readonly horseRacingHome = new RegExp(`/${this.page.lang}` + '/sports/+?([A-Za-z0-9]+?(-?[A-Za-z0-9]+)+)?-29$');
    private readonly compeRgx = new RegExp(`/${this.page.lang}` + '/sports/([A-Za-z0-9]+(-[A-Za-z0-9]+)+)/[a-zA-Z]+/([A-Za-z0-9]+(-[A-Za-z0-9]+)+)/');
    private readonly firstCouponVisit = new RegExp(`/${this.page.lang}` + '/sports/[A-Za-z0-9]+-[0-9]+/[a-zA-Z]');
    private readonly secondCouponVisit = new RegExp(
        `/${this.page.lang}` + '/sports/([A-Za-z0-9]+(-[A-Za-z0-9]+)+)/[a-zA-Z]+/([A-Za-z0-9]+(-[A-Za-z0-9]+)+)',
    );

    private readonly styleSheet = 'sports-skeleton-ui';

    private readonly sportsHomePage = new RegExp(`/${this.page.lang}` + '/sports/+?([A-Za-z0-9]+?(-?[A-Za-z0-9]+)+)?$');
    private readonly horseRacingForecast = new RegExp(
        `/${this.page.lang}` + '/sports/+([A-Za-z0-9]+(-[A-Za-z0-9]+)+)/+([A-Za-z0-9]+(-[A-Za-z0-9]+)+)',
    );

    private readonly subLivePage = new RegExp(`/${this.page.lang}` + '/sports/live/[A-Za-z0-9]+-[0-9]$');

    constructor() {
        if (this.skeletonConfig.isEnabled) {
            this.stylesService.loadStyles([{ id: this.styleSheet, type: 'theme' }]);
            this.initRouteListener();
        }
    }

    private initRouteListener(): void {
        this.router.events
            .pipe(
                this.productUtils.filterSports(),
                filter((e) => this.isValid(e)),
                withLatestFrom(this.skeletonOutput$),
                switchMap(([e, current]: any) =>
                    e instanceof NavigationStart
                        ? of(this.getPage(e.url)).pipe(delay(current === undefined ? this.skeletonConfig.skeletonAniminationStartAt : 0))
                        : of(undefined),
                ),
                distinctUntilChanged(),
            )
            .subscribe((e) => this.skeletonOutput$.next(e));
        this.skeletonOutput$
            .pipe(
                filter((e) => e !== undefined),
                this.productUtils.filterSportsEmitLast(),
                switchMap((_) => of(EMPTY).pipe(delay(this.skeletonConfig.maxtimeToSkeletonDuration))),
            )
            .subscribe((_) => this.skeletonOutput$.next(undefined));
    }

    private isValid(e: any): boolean {
        if (e instanceof NavigationStart || e instanceof NavigationCancel || e instanceof NavigationEnd || e instanceof NavigationError) {
            return true;
        }

        return false;
    }

    private getPage(url: string): SkeletonPage | undefined {
        if (url === undefined || this.eventRgx.test(url)) {
            return undefined;
        }
        url = decodeURI(url.toLowerCase());
        if (this.homeLogo.toLowerCase() === url) {
            this.slotLayoutRouteOverrideService.override(SubNavigationComponent, { fixed: false });

            return SkeletonPage.Home;
        }
        if (url.includes(this.urlConfig.translations.multiBuilder) || url.includes(this.urlConfig.translations.myBets)) {
            return undefined;
        }

        if (this.homeRgx.test(url) && this.device.isMobile) {
            return SkeletonPage.Home;
        }

        if (this.horseRacing.test(url)) {
            if (this.horseRacingHome.test(url) && !this.device.isMobile && !this.device.isTablet) {
                this.slotLayoutRouteOverrideService.override(SubNavigationComponent, { fixed: false });
            }

            if (url.includes(this.urlConfig.translations.tomorrow)) {
                return SkeletonPage.RacingTomorrow;
            }

            if (this.horseRacingForecast.test(url)) {
                return SkeletonPage.RacingForecast;
            }

            if (url.includes(this.urlConfig.translations.today)) {
                return SkeletonPage.Racing;
            }

            return SkeletonPage.Racing;
        }

        if (this.device.isMobile && this.device.isTablet) {
            if (url.includes(`${this.urlConfig.translations.live}/`)) {
                if (url.includes(`${this.urlConfig.translations.live}/${this.urlConfig.translations.betting}`)) {
                    return SkeletonPage.Favourites;
                }

                return SkeletonPage.Live;
            }

            if (
                url.includes(this.urlConfig.translations.calendar) ||
                url.includes(this.urlConfig.translations.today) ||
                url.includes(this.urlConfig.translations.coupons)
            ) {
                return SkeletonPage.Coupon;
            }
        }

        if (this.device.isMobile && !this.device.isTablet) {
            if (
                url.includes(this.urlConfig.translations.calendar) ||
                url.includes(this.urlConfig.translations.in30minutes) ||
                url.includes(this.urlConfig.translations.in60minutes) ||
                url.includes(this.urlConfig.translations.in180minutes) ||
                url.includes(this.urlConfig.translations.next2days) ||
                url.includes(this.urlConfig.translations.next3days) ||
                url.includes(this.urlConfig.translations.after2days) ||
                url.includes(this.urlConfig.translations.after3days) ||
                url.includes(this.urlConfig.translations.today) ||
                url.includes(this.urlConfig.translations.next5days) ||
                url.includes(this.urlConfig.translations.midWeek) ||
                url.includes(this.urlConfig.translations.tomorrow) ||
                url.includes(this.urlConfig.translations.future)
            ) {
                return SkeletonPage.UpComing;
            }
            if (url.includes(`${this.urlConfig.translations.live}/`)) {
                if (url.includes(`${this.urlConfig.translations.live}/${this.urlConfig.translations.betting}`)) {
                    return SkeletonPage.UpComing;
                }

                return SkeletonPage.Live;
            }

            if (url.includes(this.urlConfig.translations.competitions)) {
                const validate1 = this.firstCouponVisit.test(url);
                const validate2 = this.secondCouponVisit.test(url);
                if (url.includes(this.urlConfig.translations.favourites)) {
                    return SkeletonPage.Favourites;
                }
                if (validate1 && !validate2) {
                    return SkeletonPage.Competition;
                }
                if (validate1 && validate2) {
                    return SkeletonPage.UpComing;
                }
            }
            if (url.includes(this.urlConfig.translations.coupons)) {
                const validate1 = this.firstCouponVisit.test(url);
                const validate2 = this.secondCouponVisit.test(url);
                if (validate1 && !validate2) {
                    return SkeletonPage.Coupon;
                }
                if (validate1 && validate2) {
                    return SkeletonPage.UpComing;
                }
            }
            if (url.includes(this.urlConfig.translations.competitions)) {
                return SkeletonPage.Competition;
            }
            if (this.compeRgx.test(url)) {
                return SkeletonPage.UpComing;
            }
        }

        if (this.compeRgx.test(url)) {
            return SkeletonPage.Competition;
        }

        if (url.includes(this.urlConfig.translations.competitions)) {
            return SkeletonPage.Competition;
        } else if (
            url.includes(this.urlConfig.translations.coupons) ||
            url.includes(this.urlConfig.translations.in30minutes) ||
            url.includes(this.urlConfig.translations.in60minutes) ||
            url.includes(this.urlConfig.translations.in180minutes) ||
            url.includes(this.urlConfig.translations.next2days) ||
            url.includes(this.urlConfig.translations.next3days) ||
            url.includes(this.urlConfig.translations.after2days) ||
            url.includes(this.urlConfig.translations.after3days) ||
            url.includes(this.urlConfig.translations.next5days) ||
            url.includes(this.urlConfig.translations.midWeek)
        ) {
            return SkeletonPage.Coupon;
        } else if (url.includes(`${this.urlConfig.translations.live}/`) && !url.includes(this.urlConfig.translations.teamPages)) {
            if (url.includes(`${this.urlConfig.translations.live}/${this.urlConfig.translations.betting}`) || this.subLivePage.test(url)) {
                return SkeletonPage.Favourites;
            }

            return SkeletonPage.Live;
        } else if (url.includes(`sports/${this.urlConfig.translations.calendar}`) || url.includes(`sports/${this.urlConfig.translations.today}`)) {
            this.slotLayoutRouteOverrideService.override(SubNavigationComponent, { fixed: false });

            return SkeletonPage.Coupon;
        } else if (url.includes(this.urlConfig.translations.calendar) || url.includes(this.urlConfig.translations.today)) {
            return SkeletonPage.Coupon;
        } else if (url.includes(this.urlConfig.translations.favourites)) {
            const favCompetion = `${this.urlConfig.translations.favourites}/${this.urlConfig.translations.competitions}`;
            return url.includes(favCompetion) ? SkeletonPage.UpComing : SkeletonPage.Favourites;
        } else if (url.includes(this.urlConfig.translations.events)) {
            return SkeletonPage.Event;
        } else if (url.includes(this.urlConfig.translations.upcoming)) {
            return SkeletonPage.UpComing;
        } else if (url.includes(this.urlConfig.translations.tomorrow) || url.includes(this.urlConfig.translations.future)) {
            return SkeletonPage.Favourites;
        } else {
            if (this.homeRgx.test(url) && !this.device.isMobile) {
                this.slotLayoutRouteOverrideService.override(SubNavigationComponent, { fixed: false });

                return SkeletonPage.Home;
            }

            if (this.sportsHomePage.test(url)) {
                this.slotLayoutRouteOverrideService.override(SubNavigationComponent, { fixed: false });

                return SkeletonPage.Home;
            }
        }

        return undefined;
    }
}
